import { concat, includes, without } from 'lodash'
import * as React from 'react'

import { LOCIZE_NAMESPACE } from 'main/common'
import { useWhitelabel } from 'main/framework'
import { i18nKeyPrefix } from 'main/pages/Search/i18n'
import {
  Checkbox,
  NativeLink,
  Popover,
  QuestionIcon,
  RangeSlider,
  Strong,
  Text,
  Visible,
} from 'main/ui'
import { AssetName } from 'main/util/asset'
import { formatCurrency } from 'main/util/currency'
import { IRange } from 'main/util/range'
import {
  budgetFilterRange as monthlyBudgetFilterRange,
  IFilters,
  weeklyBudgetFilterRange,
} from 'main/util/search'
import { useTranslation } from 'react-i18next'
import {
  Apply,
  CheckboxGroupContainer,
  Clear,
  FieldsContainer,
  FilterFormContainer,
  FilterFormSubmitContainer,
} from '../../common/FilterForm.styles'
import { NetworkRatingLabel } from '../../NetworkRatingFilterV2/FilterForm/FilterForm.styles'
import { ISearchFormProps } from '../../types'
import { ChecksContainer, FieldsFormContainer, TooltipIconContainer } from './FilterForm.styles'
import Tooltip from './Tooltip'

const PRE = `${i18nKeyPrefix}-FILTERS-BUDGET-FORM`

export default function FilterForm(props: ISearchFormProps) {
  const { filters, onChangeFilters, onRequestCloseForm } = props
  const [params, setParams] = React.useState<IFilters>({ ...filters })
  const { t } = useTranslation(LOCIZE_NAMESPACE)

  return (
    <FilterFormContainer>
      <Fields params={params} onChangeParams={setParams} />
      <Visible lg xl>
        <FilterFormSubmitContainer>
          <Clear data-pendo="budgetFilter-clear" onClick={() => handleClear()}>
            {t(`${PRE}-CLEAR`)}
          </Clear>
          <Apply data-pendo="budgetFilter-apply" onClick={() => handleApply()}>
            {t(`${PRE}-APPLY`)}
          </Apply>
        </FilterFormSubmitContainer>
      </Visible>
    </FilterFormContainer>
  )

  function handleApply() {
    onRequestCloseForm()
    onChangeFilters({
      ...filters,
      budget: params.budget,
      acceptsSubsidy: params.acceptsSubsidy,
      acceptsWeeklyBilling: params.acceptsWeeklyBilling,
      selectedSubsidies: params.selectedSubsidies,
    })
  }

  function handleClear() {
    const defaultBudget = { min: null, max: null }

    setParams({
      ...params,
      budget: defaultBudget,
      acceptsSubsidy: false,
      acceptsWeeklyBilling: false,
      selectedSubsidies: [],
    })
  }
}

interface IFieldsProps {
  params: IFilters
  onChangeParams: (params: IFilters) => void
}

export function Fields(fieldsProps: IFieldsProps) {
  const { params, onChangeParams } = fieldsProps
  const whitelabel = useWhitelabel()
  const networkSubsidies = whitelabel.networkSubsidies
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const subsidyAlt = t(whitelabel.subsidyAltLookup())

  const [isTooltipOpen, setIsTooltipOpen] = React.useState<boolean>(false)
  const { acceptsSubsidy, acceptsWeeklyBilling, budget } = params
  const budgetFilterRange = acceptsWeeklyBilling
    ? weeklyBudgetFilterRange
    : monthlyBudgetFilterRange

  return (
    <FieldsFormContainer>
      <span>
        <Strong>{t(`${PRE}-LABEL`, { subsidyAlt })}</Strong>
        <Popover
          body={<Tooltip />}
          isOpen={isTooltipOpen}
          onRequestClose={() => setIsTooltipOpen(false)}
        >
          <TooltipIconContainer
            data-pendo="budgetFilter-toolTip"
            onClick={() => setIsTooltipOpen(true)}
          >
            <QuestionIcon />
          </TooltipIconContainer>
        </Popover>
      </span>
      {renderPricings()}
      {acceptsSubsidy && whitelabel.showByDefaultExceptIfFalse(AssetName.ACCEPTS_SUBSIDY) && (
        <Text css={{ fontSize: '14px' }} muted>
          {t(`${PRE}-ACCEPTED`)}
        </Text>
      )}
      <FieldsContainer>
        <RangeSlider
          data-pendo="budgetFilter-budgetFilterRange"
          range={budgetFilterRange}
          step={5000}
          value={budgetValue()}
          onChange={handleBudgetChange}
        />
      </FieldsContainer>
      <ChecksContainer>
        <Checkbox
          data-pendo="weeklyFilter-acceptsWeekly"
          checked={acceptsWeeklyBilling}
          label={t(`${PRE}-WEEKLY`)}
          onChange={handleWeeklyCheckboxToggle}
          disabled={acceptsSubsidy}
        />
        <Checkbox
          data-pendo="subsidyFilter-acceptsSubsidy"
          checked={acceptsSubsidy}
          label={t(`${PRE}-ACCEPTS-SUBSIDY`, { subsidyAlt: subsidyAlt.toLowerCase() })}
          onChange={handleSubsidyCheckboxToggle}
        />
      </ChecksContainer>

      {!!networkSubsidies.length &&
        acceptsSubsidy &&
        whitelabel.hideByDefaultExceptIfTrue(AssetName.SUBSIDY_BUDGET_FILTER) && (
          <>
            <Strong>{t(`${PRE}-SUBSIDY-SECTION-TITLE`, { subsidyAlt })}</Strong>
            <FieldsContainer>
              <CheckboxGroupContainer css={{ width: 'max-content' }}>
                {networkSubsidies.map(option => {
                  return (
                    <Checkbox
                      key={option.id}
                      checked={includes(params.selectedSubsidies, option.id)}
                      label={
                        <NetworkRatingLabel>
                          <span>{option.name}</span>
                        </NetworkRatingLabel>
                      }
                      onChange={(checked: boolean) => {
                        checked
                          ? onChangeParams({
                              ...params,
                              selectedSubsidies: concat(params.selectedSubsidies, option.id),
                            })
                          : onChangeParams({
                              ...params,
                              selectedSubsidies: without(params.selectedSubsidies, option.id),
                            })
                      }}
                    />
                  )
                })}
              </CheckboxGroupContainer>
            </FieldsContainer>
          </>
        )}

      {whitelabel.hideByDefaultExceptIfTrue(AssetName.SUBSIDY_BUDGET_FILTER) && (
        <NativeLink
          href="https://brighterfuturesindiana.org/parents/financial-support/help-paying-for-child-care"
          target="_blank"
        >
          {t(`${PRE}-SUBSIDY-SECTION-LEARN-MORE`)}
        </NativeLink>
      )}
    </FieldsFormContainer>
  )

  function handleWeeklyCheckboxToggle(checked: boolean) {
    if (checked) {
      const newBudget = {
        max: budget.max && budget.max !== budgetFilterRange.max ? budget.max / 4 : null,
        min: budget.min && budget.min !== budgetFilterRange.min ? budget.min / 4 : null,
      }
      onChangeParams({
        ...params,
        budget: newBudget,
        acceptsWeeklyBilling: checked,
        acceptsSubsidy: false,
      })
    } else {
      const defaultBudget = { min: null, max: null }
      onChangeParams({ ...params, budget: defaultBudget, acceptsWeeklyBilling: checked })
    }
  }

  function handleSubsidyCheckboxToggle(checked: boolean) {
    if (checked) {
      onChangeParams({ ...params, acceptsSubsidy: checked, acceptsWeeklyBilling: false })
    } else {
      onChangeParams({ ...params, acceptsSubsidy: checked })
    }
  }

  function budgetValue() {
    return {
      max: budget.max === null ? budgetFilterRange.max : budget.max,
      min: budget.min === null ? budgetFilterRange.min : budget.min,
    }
  }

  function handleBudgetChange(change: IRange<number>) {
    const newBudget = {
      max: change.max === budgetFilterRange.max ? null : change.max,
      min: change.min === budgetFilterRange.min ? null : change.min,
    }

    onChangeParams({ ...params, budget: newBudget })
  }

  function formatLabel(min: number, max: number, isMax: boolean) {
    return `${formatCurrency(min)} - ${formatCurrency(max)}${isMax ? '+' : ''}`
  }

  function renderPricings() {
    const min = budget.min === null ? budgetFilterRange.min : budget.min
    const max = budget.max === null ? budgetFilterRange.max : budget.max
    const weeklyMin = min / 4
    const weeklyMax = max / 4
    const isMaxWeekly = weeklyMax === weeklyBudgetFilterRange.max
    const isMax =
      (acceptsWeeklyBilling && max === weeklyBudgetFilterRange.max) ||
      max === monthlyBudgetFilterRange.max

    return (
      <>
        <Text>{`${formatLabel(min, max, isMax)}/${
          acceptsWeeklyBilling ? t(`${PRE}-WEEK`) : t(`${PRE}-MONTH`)
        }`}</Text>
        {!acceptsWeeklyBilling && !acceptsSubsidy && (
          <Text css={{ fontSize: '14px' }} muted>
            {`${formatLabel(weeklyMin, weeklyMax, isMaxWeekly)}/${t(`${PRE}-WEEK`)}`}
          </Text>
        )}
      </>
    )
  }
}
